import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAppStore } from '../../../store/appStore';
import { MatterDetailed, MatterFilters } from '../../../types/matters';
import { LexAssistantWidget } from '../ai/LexAssistantWidget.tsx';
import { useLexChatStore } from '../ai/lexChatStore.ts';
import { useSearch } from '../../../contexts/SearchContext';
import KpiSummaryBar from '../components/KpiSummaryBar.tsx';
import MattersControlsBar from '../components/MattersControlsBar.tsx';
import MattersTableView from '../components/MattersTableView.tsx';
import MattersCardView from '../components/MattersCardView.tsx';
import MattersKanbanView from '../components/MattersKanbanView.tsx';
import MatterCreateForm from '../components/MatterCreateForm.tsx';
import MatterDetailDashboard from './MatterDetailDashboard.tsx';
import { useMatters } from '../hooks/useMatters'; // Use real API hook instead of mock
import { useWindowResize } from '../../../hooks/useWindowResize';

import { useUserPreferencesStore } from '@/store/userPreferencesStore';

/**
 * Main Matters Dashboard
 * Multi-view dashboard with KPI summary, controls, and Lex AI integration
 * Now using real API integration with MCP workflows
 */
const MattersDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isSidebarExpanded } = useAppStore();
  const { isLexOpen, isLexPinned, setLexOpen } = useLexChatStore();
  const { debouncedSearchQuery } = useSearch();
  const { matters, fetchMatters, updateMatter, loading, error } = useMatters(); // Use real API hook with matters data
  const [currentMatter, setCurrentMatter] = useState<MatterDetailed | null>(null);
  const { appPreferences, hasHydrated } = useUserPreferencesStore();
  const [currentView, setCurrentView] = useState<'table' | 'card' | 'kanban'>(
    appPreferences.matters.defaultView,
  );
  const lastAppliedView = useRef(appPreferences.matters.defaultView);
  const [matterFilters, setMatterFilters] = useState<MatterFilters>({
    status: '',
    practice_area: '',
    assigned_attorney_id: '',
    search: ''
  });

  // Animation states for chat UI
  const [isAnimating, setIsAnimating] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);

  // Window width for responsive behavior
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Header height for dynamic positioning
  const [headerHeight, setHeaderHeight] = useState(64); // Default fallback

  // Manual resize state for chat width
  const [customChatWidth, setCustomChatWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  // Ref for click outside detection
  const chatRef = useRef<HTMLDivElement>(null);
  // Ref for resize handle
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  // Calculate KPI metrics
  const kpiMetrics = {
    totalMatters: (matters || []).length,
    activeMatters: (matters || []).filter(m => m.status === 'active').length,
    pendingMatters: (matters || []).filter(m => m.status === 'pending').length,
    recentlyClosed: (matters || []).filter(m => {
      if (m.status !== 'closed') return false;
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const matter = m as MatterDetailed;
      return matter.closeDate && new Date(matter.closeDate) >= thirtyDaysAgo;
    }).length
  };

  useEffect(() => {
    if (!hasHydrated) return;
    const preferredView = appPreferences.matters.defaultView;
    if (currentView === lastAppliedView.current && preferredView !== currentView) {
      setCurrentView(preferredView);
    }
    lastAppliedView.current = preferredView;
  }, [appPreferences.matters.defaultView, currentView, hasHydrated]);

  // Load matters with current filters and search
  const loadMatters = useCallback(async () => {
    try {
      const filters = {
        ...matterFilters,
        search: debouncedSearchQuery
      };
      
      // Remove empty filter values
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      await fetchMatters(cleanFilters);
    } catch (error) {
      console.error('Failed to load matters:', error);
    }
  }, [fetchMatters, matterFilters, debouncedSearchQuery]);

  // Reload matters when filters or search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      loadMatters();
    }, 300); // Debounce API calls

    return () => clearTimeout(timer);
  }, [loadMatters]);

  // Animation logic for chat UI - FIXED: Properly sync with store changes
  useEffect(() => {
    console.log('🔄 [MattersDashboard] Store state changed:', { isLexOpen, isLexPinned });
    
    if (isLexOpen || isLexPinned) {
      setChatVisible(true);
      // Use requestAnimationFrame for smooth animation without noticeable delay
      const animationFrame = requestAnimationFrame(() => setIsAnimating(true));
      return () => cancelAnimationFrame(animationFrame);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => setChatVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isLexOpen, isLexPinned]); // ✅ FIXED: React to ALL store changes

  // Escape key handler for closing chat (only when unpinned)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isLexOpen && !isLexPinned) {
        setLexOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isLexOpen, isLexPinned, setLexOpen]);

  // Click outside to close chat (only when unpinned and open)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only handle click outside when chat is open but not pinned
      // If pinned, user should use the pin button to close
      if (isLexOpen && !isLexPinned && chatRef.current && 
          !chatRef.current.contains(event.target as Node)) {
        setLexOpen(false);
      }
    };
    
    // Only add listener when chat is in the correct state
    if (isLexOpen && !isLexPinned) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLexOpen, isLexPinned, setLexOpen]);

  // Memoized window resize handler to prevent unnecessary hook re-executions
  const handleWindowResize = useCallback(() => {
    const newWindowWidth = window.innerWidth;
    setWindowWidth(newWindowWidth);
    
    // Validate and adjust customChatWidth if it exceeds new window constraints
    if (customChatWidth && newWindowWidth >= 768) {
      // More conservative limits: ensure main content gets at least 50% of screen
      const maxAllowedWidth = Math.min(600, newWindowWidth * 0.5); // Max 50% of window width or 600px
      const minAllowedWidth = 280;
      
      if (customChatWidth > maxAllowedWidth) {
        setCustomChatWidth(maxAllowedWidth);
      } else if (customChatWidth < minAllowedWidth) {
        setCustomChatWidth(minAllowedWidth);
      }
    }
    
    // Also update header height when window resizes
    const header = document.querySelector('header, [data-testid="header"], .header');
    if (header) {
      setHeaderHeight(header.getBoundingClientRect().height);
    }
  }, [customChatWidth, setCustomChatWidth, setWindowWidth, setHeaderHeight]);

  // Handle window resize for responsive behavior
  useWindowResize(handleWindowResize, 150); // 150ms debounce for better performance

  // Measure header height on mount and when DOM changes
  useEffect(() => {
    const measureHeaderHeight = () => {
      const header = document.querySelector('header, [data-testid="header"], .header');
      if (header) {
        setHeaderHeight(header.getBoundingClientRect().height);
      }
    };

    // Measure immediately
    measureHeaderHeight();

    // Set up observer for header height changes
    const observer = new ResizeObserver(measureHeaderHeight);
    const header = document.querySelector('header, [data-testid="header"], .header');
    if (header) {
      observer.observe(header);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeStartX(e.clientX);
    
    // Get current chat width using the same logic as the actual chat width calculation
    const currentWidth = getChatWidthInPixels();
    setResizeStartWidth(currentWidth);
    
    // Prevent text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  };

  // Handle resize during mouse move
  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const deltaX = resizeStartX - e.clientX;
      // More conservative max: ensure main content gets at least 50% of screen
      const maxAllowedWidth = Math.min(600, windowWidth * 0.5); // Max 50% of window width or 600px
      const minAllowedWidth = 280;
      const newWidth = Math.max(minAllowedWidth, Math.min(maxAllowedWidth, resizeStartWidth + deltaX));
      
      setCustomChatWidth(newWidth);
    };

    const handleResizeEnd = () => {
      if (isResizing) {
        setIsResizing(false);
        // Restore body styles
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, resizeStartX, resizeStartWidth, windowWidth]);

  // Calculate chat width in pixels
  const getChatWidthInPixels = (): number => {
    if (customChatWidth !== null) {
      return customChatWidth;
    }
    // Default width: 40% of window width, but max 480px and min 280px
    return Math.max(280, Math.min(480, windowWidth * 0.4));
  };

  // Calculate main content margin based on chat state and sidebar state
  const getMainContentMargin = (): string => {
    if (!chatVisible || windowWidth < 768) return '0';
    if (isLexPinned) {
      return `${getChatWidthInPixels()}px`;
    }
    return '0';
  };

  // Filtered matters based on search and filters
  const filteredMatters = (matters || []).filter((matter) => {
    const detailedMatter = matter as MatterDetailed;
    
    const matchesSearch = !debouncedSearchQuery || 
      matter.title?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      matter.matter_number?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      detailedMatter.displayNumber?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      detailedMatter.client?.name?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      matter.practice_area?.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
    
    const matchesStatus = !matterFilters.status || matter.status === matterFilters.status;
    const matchesPracticeArea = !matterFilters.practice_area || matter.practice_area === matterFilters.practice_area;
    const matchesAttorney = !matterFilters.assigned_attorney_id || matter.assigned_attorney_id === matterFilters.assigned_attorney_id;
    
    return matchesSearch && matchesStatus && matchesPracticeArea && matchesAttorney;
  }) as MatterDetailed[];

  // Handle matter status change (for Kanban view) with real API
  const handleMatterStatusChange = async (matterId: string, newStatus: 'active' | 'pending' | 'closed' | 'on_hold') => {
    try {
      // Update via API
      await updateMatter(matterId, { status: newStatus });
      console.log(`Matter ${matterId} status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update matter status:', error);
    }
  };

  // Handle matter loaded callback for AI context
  const handleMatterLoaded = useCallback((matter: MatterDetailed) => {
    setCurrentMatter(matter);
  }, []);

  const renderCurrentView = () => {
    switch (currentView) {
      case 'table':
        return (
          <MattersTableView
            matters={filteredMatters}
            loading={loading}
            onMatterClick={(matter: MatterDetailed) => navigate(`/engagements/${matter.id}`)}
            searchQuery={debouncedSearchQuery}
            onRefreshNeeded={loadMatters}
          />
        );
      case 'card':
        return (
          <MattersCardView
            matters={filteredMatters}
            loading={loading}
            onMatterClick={(matter: MatterDetailed) => navigate(`/engagements/${matter.id}`)}
            searchQuery={debouncedSearchQuery}
          />
        );
      case 'kanban':
        return (
          <MattersKanbanView
            matters={filteredMatters}
            loading={loading}
            onStatusChange={handleMatterStatusChange}
            onMatterClick={(matter: MatterDetailed) => navigate(`/engagements/${matter.id}`)}
          />
        );
      default:
        return null;
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">
            Error Loading Engagements
          </h2>
          <p className="text-gray-600 dark:text-gray-300">{error}</p>
          <button
            onClick={loadMatters}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-white dark:bg-gray-900 transition-colors duration-300 ease-in-out">
      {/* Main Content */}
      <div 
        className="flex-1 flex flex-col overflow-hidden transition-all duration-300 ease-in-out"
        style={{ 
          marginRight: getMainContentMargin()
        }}
      >
        <Routes>
          <Route index element={
            <div className="flex flex-col h-full">
              {/* KPI Summary */}
              {appPreferences.matters.showKpiSummary && (
                <KpiSummaryBar
                  totalMatters={kpiMetrics.totalMatters}
                  activeMatters={kpiMetrics.activeMatters}
                  pendingMatters={kpiMetrics.pendingMatters}
                  recentlyClosed={kpiMetrics.recentlyClosed}
                />
              )}

              {/* Controls Bar */}
              <MattersControlsBar
                currentView={currentView}
                onViewChange={setCurrentView}
                filters={matterFilters}
                onFiltersChange={(partialFilters: Partial<MatterFilters>) => setMatterFilters(prev => ({ ...prev, ...partialFilters }))}
                totalCount={filteredMatters.length}
                onNewMatter={() => {
                  localStorage.removeItem('webelieve-engagement-draft');
                  navigate('/engagements/new');
                }}
                chatVisible={chatVisible}
                isLexPinned={isLexPinned}
                isSidebarExpanded={isSidebarExpanded}
                windowWidth={windowWidth}
              />

              {/* Current View */}
              <div className="flex-1 overflow-auto">
                {renderCurrentView()}
              </div>
            </div>
          } />
          
          <Route path="new" element={<MatterCreateForm />} />
          <Route path=":matterId" element={<MatterDetailDashboard onMatterLoaded={handleMatterLoaded} />} />
          <Route path=":matterId/edit" element={<MatterCreateForm />} />
        </Routes>
      </div>

      {/* Lex AI Assistant Widget */}
      {chatVisible && (
        <div
          ref={chatRef}
          className={`fixed right-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col z-40 transition-all duration-300 ease-in-out ${
            isAnimating ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
          }`}
          style={{
            top: `${headerHeight}px`,
            height: `calc(100vh - ${headerHeight}px)`,
            width: windowWidth >= 768 ? `${getChatWidthInPixels()}px` : '100vw',
          }}
        >
          {/* Resize Handle */}
          {windowWidth >= 768 && (
            <div
              ref={resizeHandleRef}
              className="absolute left-0 top-0 w-1 h-full cursor-ew-resize hover:bg-lex-accent hover:w-1.5 transition-all duration-200 bg-gray-300 dark:bg-gray-600"
              onMouseDown={handleResizeStart}
              title="Drag to resize chat"
            />
          )}

          <LexAssistantWidget currentMatter={currentMatter} containerMode="sidebar" />
        </div>
      )}

      {/* Floating Lex AI launcher button: Only show if chat is closed */}
      {!(isLexOpen || isLexPinned) && (
        <button
          onClick={() => setLexOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg bg-lex-accent text-lex-accent-contrast flex items-center justify-center transition-all duration-300 hover:scale-105 hover:bg-lex-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent"
          title="Open Lex AI Assistant"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default MattersDashboard;
